export const BOT_NAME = "Navodya's Assistant";

export const GREETING =
  "Hi! I'm Navodya's virtual assistant 👋 I can answer questions about her skills, experience, projects, education, and more. What would you like to know?";

export const FALLBACK =
  "Hmm, I'm not sure about that one. Try asking about Navodya's **skills**, **experience**, **projects**, **education**, or **contact** details. You can also use the quick suggestions below!";

// Quick reply chips shown after greeting and fallback
export const QUICK_REPLIES = [
  "Who is Navodya?",
  "What are her skills?",
  "Show me her projects",
  "How can I contact her?",
  "Is she available to hire?",
];

/*
  Each entry:
    keywords  — exact substring matches in the lowercased input (weight 1 each)
    phrases   — multi-word phrases that score higher when fully matched (weight 3 each)
    response  — the reply text (supports **bold**)
    chips     — optional quick-reply follow-ups after this response
*/
export const responses = [
  // ── Greetings ──────────────────────────────────────────────────────────────
  {
    keywords: ["hi", "hello", "hey", "yo", "hiya", "howdy", "greetings", "sup"],
    phrases: ["good morning", "good afternoon", "good evening", "what's up"],
    response:
      "Hello there! 😊 Great to have you here. Ask me anything about Navodya — her work, skills, projects, or how to get in touch!",
    chips: ["Who is Navodya?", "Her skills", "Contact her"],
  },

  // ── Who is she / About ──────────────────────────────────────────────────────
  {
    keywords: ["who", "about", "navodya", "navodya", "yourself", "introduce", "person", "bio", "background", "profile"],
    phrases: ["who is she", "tell me about", "who are you", "about navodya", "about her"],
    response:
      "**Navodya (Navodya) Fernando** is a Full Stack Software Engineer with 3+ years of professional experience 🚀\n\nShe specializes in building scalable enterprise systems across **aviation, logistics, HR, healthcare, and AI** domains. Currently based in **Dubai, UAE**, she works at UUDS Aero DWC LLC as a Software Engineer, tackling complex backend architectures, product ownership, and cloud deployments.\n\nShe's passionate about clean code, system design, and continuous learning.",
    chips: ["Her skills", "Work experience", "Her projects"],
  },

  // ── Location ────────────────────────────────────────────────────────────────
  {
    keywords: ["location", "where", "based", "live", "city", "country", "dubai", "uae", "sri lanka", "relocate"],
    phrases: ["where is she", "where does she live", "where is navodya based", "can she relocate"],
    response:
      "Navodya is currently based in **Dubai Internet City (DIC), UAE** 🇦🇪\n\nShe's originally from Sri Lanka 🇱🇰 and moved to Dubai for her current role. She's open to discussing relocation opportunities — reach out to her directly!",
    chips: ["Contact her", "Current job"],
  },

  // ── Skills (overview) ───────────────────────────────────────────────────────
  {
    keywords: ["skill", "tech", "stack", "technology", "tools", "expertise", "proficient", "capable", "competency", "know", "use", "good at"],
    phrases: ["tech stack", "what skills", "what technologies", "what does she know", "what can she do", "what tools"],
    response:
      "Navodya's tech stack is quite broad:\n\n🎨 **Frontend:** React, Next.js, Angular, TypeScript\n⚙️ **Backend:** NestJS, ASP.NET Core, Node.js/Express, Django (DRF)\n🗄️ **Databases:** PostgreSQL, MongoDB, MySQL, Redis, MSSQL\n☁️ **Cloud & DevOps:** AWS, Azure, Docker, CI/CD pipelines\n🔐 **Auth & API:** JWT, OAuth2, REST, GraphQL, RBAC\n🧠 **AI/ML:** TensorFlow, OpenCV, Python, NLP\n📦 **Other:** Redux, NgRx, Celery, Agile/Scrum",
    chips: ["Frontend skills", "Backend skills", "DevOps skills", "AI skills"],
  },

  // ── Frontend ────────────────────────────────────────────────────────────────
  {
    keywords: ["react", "next", "nextjs", "angular", "frontend", "ui", "interface", "css", "html", "tailwind", "bootstrap", "redux", "ngrx", "typescript"],
    phrases: ["front end", "react skills", "frontend experience", "angular experience", "next.js"],
    response:
      "On the **frontend**, Navodya works with:\n\n• **React** — multiple production apps including HR platforms and AI dashboards\n• **Next.js** — enterprise document tracking systems with server-side rendering\n• **Angular + NgRx** — real-time freight and travel booking platforms\n• **TypeScript** — used across all modern projects\n• **Redux** — state management in full-stack MERN apps\n• Styling: Tailwind CSS, Bootstrap, CSS Modules",
    chips: ["Backend skills", "Show her projects"],
  },

  // ── Backend ─────────────────────────────────────────────────────────────────
  {
    keywords: ["backend", "server", "api", "rest", "nestjs", "nest", "node", "nodejs", "express", "aspnet", "asp.net", "dotnet", ".net", "django", "python", "spring"],
    phrases: ["back end", "server side", "api development", "backend experience", "nestjs experience"],
    response:
      "On the **backend**, Navodya has strong expertise in:\n\n• **NestJS** — clean architecture, modular design, microservices\n• **ASP.NET Core (C#)** — RESTful APIs with JWT & RBAC\n• **Node.js / Express** — MERN stack applications\n• **Django + DRF** — Python-based REST APIs with Celery/Redis for async tasks\n• All with proper authentication, input validation, and error handling",
    chips: ["Database skills", "DevOps skills"],
  },

  // ── Databases ───────────────────────────────────────────────────────────────
  {
    keywords: ["database", "db", "sql", "postgres", "postgresql", "mongodb", "mongo", "mysql", "redis", "mssql", "data", "schema", "query", "orm"],
    phrases: ["database experience", "what databases", "sql experience"],
    response:
      "Navodya works with a range of databases:\n\n• **PostgreSQL** — primary DB for enterprise apps, optimized schema design\n• **MongoDB** — MERN stack and document-based storage\n• **MySQL / MSSQL** — relational DBs for enterprise systems\n• **Redis** — caching and async queue management with Celery\n\nShe focuses on optimized query design, indexing, and normalized schemas.",
    chips: ["Backend skills", "Her projects"],
  },

  // ── Cloud / DevOps ──────────────────────────────────────────────────────────
  {
    keywords: ["docker", "aws", "azure", "cloud", "devops", "deploy", "cicd", "ci/cd", "pipeline", "container", "kubernetes", "linux", "server"],
    phrases: ["cloud experience", "devops experience", "docker experience", "aws experience", "azure experience"],
    response:
      "Navodya has hands-on cloud and DevOps experience:\n\n☁️ **AWS** — EC2, deployments, cloud scalability (SPELL HR platform)\n🔵 **Azure** — Deployed FireFox Plus freight platform (40% efficiency boost)\n🐳 **Docker** — Containerized multiple enterprise applications\n🔁 **CI/CD** — Automated pipelines for build, test, and deployment\n🐧 **Linux** — Server-side operations and deployments",
    chips: ["Her projects", "Backend skills"],
  },

  // ── AI / ML ─────────────────────────────────────────────────────────────────
  {
    keywords: ["ai", "ml", "machine learning", "tensorflow", "opencv", "nlp", "deep learning", "computer vision", "neural", "model", "artificial intelligence"],
    phrases: ["ai experience", "machine learning experience", "computer vision", "ai projects"],
    response:
      "Yes, Navodya has **AI/ML experience** too! 🤖\n\n• **AI Computer Vision System** at Sri Lanka Telecom — customer experience measurement using TensorFlow and OpenCV\n• **Biomedical System** — DICOM medical imaging with OpenCV and NumPy\n• **Social Media Recommendation System** — NLP + deep learning for candidate profiling\n• **Coin Sorting System** — MATLAB-based image processing and classification\n\nHer AI work spans computer vision, NLP, and data processing.",
    chips: ["Show AI projects", "Her projects"],
  },

  // ── C# / .NET ───────────────────────────────────────────────────────────────
  {
    keywords: ["c#", "csharp", "dotnet", ".net", "asp.net", "aspnet"],
    phrases: [".net core", "asp.net core", "c sharp"],
    response:
      "Navodya has solid **C# / ASP.NET Core** experience:\n\n• **TalentCore** (HR platform) — ASP.NET Core REST APIs with JWT/RBAC\n• **Travelogue** (travel booking) — .NET Core + C# with Stripe integration\n• **FireFox Plus** (freight forwarding) — .NET Core backend, Azure-deployed\n\nShe's built full enterprise-grade systems end-to-end with .NET.",
    chips: ["Show .NET projects", "Her projects"],
  },

  // ── Python / Django ─────────────────────────────────────────────────────────
  {
    keywords: ["python", "django", "drf", "celery", "fastapi", "flask"],
    phrases: ["django rest", "python experience", "django experience"],
    response:
      "Navodya uses **Python and Django** for backend development:\n\n• **SPELL (HR platform)** — Django + DRF + Celery + Redis + Docker + AWS\n• **Biomedical System** — Python with NumPy and OpenCV for medical imaging\n• **Social Media Analysis** — Python-based NLP and deep learning research\n\nShe's comfortable with async processing, REST APIs, and data pipelines in Python.",
    chips: ["SPELL project", "Her projects"],
  },

  // ── Work Experience (overview) ──────────────────────────────────────────────
  {
    keywords: ["experience", "work", "job", "career", "company", "employ", "professional", "worked", "history", "background"],
    phrases: ["work experience", "job history", "career history", "where has she worked", "work background", "professional experience"],
    response:
      "Navodya's professional journey:\n\n🏢 **UUDS Aero DWC LLC** — Software Engineer (2025–Present), Dubai\n🏢 **Hayleys Advantis (PVT) Ltd** — Software Engineer (2024–2025), Sri Lanka\n🏢 **Sri Lanka Telecom** — Software Engineer Intern (2023), Sri Lanka\n\nShe's worked across **aviation, logistics, HR, healthcare, and AI** — building production systems used by real organizations.",
    chips: ["UUDS Aero", "Hayleys Advantis", "Sri Lanka Telecom"],
  },

  // ── UUDS Aero ───────────────────────────────────────────────────────────────
  {
    keywords: ["uuds", "aero", "aviation", "current job", "current role", "current company", "current work", "now", "present"],
    phrases: ["uuds aero", "current position", "where does she work now", "current employer"],
    response:
      "Navodya currently works at **UUDS Aero DWC LLC** in Dubai as a Software Engineer (2025–Present).\n\nHer work there includes:\n• **DocuTrack** — Document tracking system (NestJS, Next.js, PostgreSQL)\n• **UUDS LMS** — Aviation-compliant learning management system (Product Owner)\n• **SPELL** — Human Capital Platform (Django, AWS, Docker)\n• **TalentCore** — HR Management System (React, ASP.NET Core)\n\nShe handles both backend architecture and product ownership.",
    chips: ["DocuTrack project", "SPELL project", "Her projects"],
  },

  // ── Hayleys Advantis ────────────────────────────────────────────────────────
  {
    keywords: ["hayleys", "advantis", "freight", "logistics", "travel", "travelogue", "firefox"],
    phrases: ["hayleys advantis", "freight forwarding", "travel booking"],
    response:
      "At **Hayleys Advantis (PVT) Ltd** (2024–2025), Navodya built two major platforms:\n\n• **Travelogue** — Full-stack travel management system (.NET Core, Angular, Stripe)\n• **FireFox Plus** — Enterprise freight forwarding platform (.NET Core, Angular, NgRx, Azure) — improved operational efficiency by **40%**",
    chips: ["Travelogue project", "FireFox Plus project"],
  },

  // ── Sri Lanka Telecom ───────────────────────────────────────────────────────
  {
    keywords: ["telecom", "slt", "sri lanka telecom", "intern", "internship"],
    phrases: ["sri lanka telecom", "slt intern"],
    response:
      "At **Sri Lanka Telecom** (2023) as a Software Engineer Intern, Navodya built:\n\n• **AI Computer Vision System** — Customer experience measurement using TensorFlow, OpenCV, AWS\n• **Biomedical System** — MERN stack app for medical data processing with DICOM and OpenCV",
    chips: ["AI project", "Her projects"],
  },

  // ── Projects (overview) ─────────────────────────────────────────────────────
  {
    keywords: ["project", "build", "built", "develop", "portfolio", "work on", "created", "made", "app", "application", "system", "platform"],
    phrases: ["show me projects", "what projects", "all projects", "her projects", "what has she built"],
    response:
      "Navodya has built 13+ projects across diverse domains! Here are the highlights:\n\n🏢 **DocuTrack** — Document management (NestJS, Next.js)\n👩‍💼 **SPELL** — HR & Payroll platform (Django, AWS)\n✈️ **UUDS LMS** — Aviation learning management\n🧑‍💼 **TalentCore** — HR system (React, ASP.NET)\n✈️ **Travelogue** — Travel booking (.NET, Angular)\n🚢 **FireFox Plus** — Freight platform (Azure)\n🤖 **AI Vision** — Computer vision (TensorFlow)\n🏥 **Biomedical System** — Medical imaging (MERN)\n\nAsk about any specific one!",
    chips: ["DocuTrack", "SPELL project", "AI project", "FireFox Plus"],
  },

  // ── DocuTrack ───────────────────────────────────────────────────────────────
  {
    keywords: ["docutrack", "document tracking", "document management", "engineering documentation"],
    phrases: ["document system", "docutrack project"],
    response:
      "**DocuTrack** — Document Tracking System (Jan 2026–Present)\n\n📍 UUDS Aero DWC LLC\n\nA centralized system for controlled technical and engineering documentation with secure approval workflows.\n\n🛠 Tech: **NestJS, Next.js, PostgreSQL, JWT, Linux, Cloud**\n\nKey features: modular clean architecture, RBAC with granular permissions, audit logging, version control, full document lifecycle (submission → review → approval → archival).",
    chips: ["SPELL project", "Other projects"],
  },

  // ── SPELL ───────────────────────────────────────────────────────────────────
  {
    keywords: ["spell", "human capital", "payroll", "attendance", "leave management", "hr platform"],
    phrases: ["spell project", "hr platform", "payroll system"],
    response:
      "**SPELL** — Human Capital Platform (Sep 2025–Present)\n\n📍 UUDS Aero DWC LLC\n\nScalable HR management platform covering employee lifecycle, payroll, attendance, and leave management.\n\n🛠 Tech: **Python, Django, DRF, PostgreSQL, Celery, Redis, Docker, AWS**\n\nHighlights: JWT/OAuth2 + RBAC, async processing with Celery & Redis, Docker containerization, AWS deployment, CI/CD pipelines.",
    chips: ["TalentCore project", "Her projects"],
  },

  // ── TalentCore ──────────────────────────────────────────────────────────────
  {
    keywords: ["talentcore", "talent core"],
    phrases: ["talentcore project", "hr management system"],
    response:
      "**TalentCore** — HR Management System (Jun–Dec 2025)\n\n📍 UUDS Aero DWC LLC\n\nFull-stack HR platform for employee management, payroll, leave tracking, and attendance.\n\n🛠 Tech: **React, ASP.NET Core, PostgreSQL, JWT, Docker, AWS**\n\nFeatures: Scalable REST APIs, reusable React components, normalized schemas, Docker + AWS CI/CD deployments.",
    chips: ["SPELL project", "Her projects"],
  },

  // ── Travelogue ──────────────────────────────────────────────────────────────
  {
    keywords: ["travelogue", "travel management", "hotel booking", "flight booking", "stripe", "payment"],
    phrases: ["travelogue project", "travel system"],
    response:
      "**Travelogue** — Travel Management System (Oct 2024–Apr 2025)\n\n📍 Hayleys Advantis (PVT) Ltd\n\nFull-stack travel platform handling customer sign-up, flight & hotel bookings, and payments.\n\n🛠 Tech: **.NET Core, C#, Angular, SQL, Stripe, JWT**\n\nFeatures: Stripe payment integration, JWT-based RBAC, optimized DB queries with caching for high-traffic load.",
    chips: ["FireFox Plus project", "Her projects"],
  },

  // ── FireFox Plus ────────────────────────────────────────────────────────────
  {
    keywords: ["firefox", "firefox plus", "freight", "shipment", "logistics platform", "forwarding"],
    phrases: ["firefox plus", "freight platform", "freight forwarding"],
    response:
      "**FireFox Plus** — Freight Forwarding Platform (Jul 2024–Jan 2025)\n\n📍 Hayleys Advantis (PVT) Ltd\n\nEnterprise freight platform with RESTful APIs for shipment booking, tracking, invoicing, and user management.\n\n🛠 Tech: **.NET Core, C#, Angular, NgRx, MySQL, JWT, Azure**\n\n⭐ Deployed on Azure — improved operational efficiency by **40%**. Real-time updates via NgRx state management.",
    chips: ["Travelogue project", "Her projects"],
  },

  // ── AI Vision ───────────────────────────────────────────────────────────────
  {
    keywords: ["computer vision", "ai vision", "customer experience", "opencv", "tensorflow", "slt project"],
    phrases: ["ai project", "computer vision project", "vision system"],
    response:
      "**AI Computer Vision** — Customer Experience Measurement (Jul–Dec 2023)\n\n📍 Sri Lanka Telecom\n\nAI-powered customer experience measurement using computer vision for real-time analytics.\n\n🛠 Tech: **ReactJS, NodeJS, ExpressJS, MongoDB, TensorFlow, OpenCV, AWS**\n\nBuilt the React frontend, Node.js/MongoDB backend for analytics, and integrated AWS for cloud scalability.",
    chips: ["Biomedical project", "Her projects"],
  },

  // ── Biomedical ──────────────────────────────────────────────────────────────
  {
    keywords: ["biomedical", "medical", "dicom", "healthcare", "imaging", "numpy"],
    phrases: ["biomedical project", "medical system"],
    response:
      "**Biomedical System** (Jul–Dec 2023)\n\n📍 Sri Lanka Telecom\n\nFull-stack MERN application for processing and visualizing medical data.\n\n🛠 Tech: **ReactJS, NodeJS, ExpressJS, MongoDB, Python, NumPy, DICOM, OpenCV**\n\nUsed DICOM for medical imaging standards, OpenCV for image processing, NumPy for data manipulation.",
    chips: ["AI project", "Her projects"],
  },

  // ── Medical Center System ───────────────────────────────────────────────────
  {
    keywords: ["medical center", "university hospital", "ruhuna medical", "final year project", "fyp"],
    phrases: ["final year project", "medical center system"],
    response:
      "**Medical Center Administrative System** (Jan–May 2023)\n\n📍 University of Ruhuna (Final Year Project)\n\nFull-stack MERN application for end-to-end administrative management of the university medical center.\n\n🛠 Tech: **ReactJS, NodeJS, ExpressJS, MongoDB, JWT, Redux**\n\nFeatures: RESTful APIs, JWT authentication, Redux state management.",
    chips: ["Her projects"],
  },

  // ── Hackathon / Voting ──────────────────────────────────────────────────────
  {
    keywords: ["hackathon", "sliit", "codefest", "voting", "election"],
    phrases: ["sliit codefest", "voting system", "election system"],
    response:
      "**Election Voting Management System** — SLIIT CODEFEST Hackathon 2022 🏆\n\nSecure voting platform built during the SLIIT CODEFEST Hackathon Finals. Navodya led the team as **Team Leader**.\n\n🛠 Tech: **PHP, jQuery, Ajax, SQL**\n\nFeatures: Backend security, SQL database management, and asynchronous communication for real-time voting experience.",
    chips: ["Her projects"],
  },

  // ── LMS ─────────────────────────────────────────────────────────────────────
  {
    keywords: ["lms", "learning management", "aviation training", "gcaa", "easa", "faa", "certification", "product owner"],
    phrases: ["learning management system", "aviation lms", "uuds lms"],
    response:
      "**UUDS Learning (LMS)** — Aviation Learning Management System (Nov 2025–Present)\n\nNavodya acts as **Product Owner** for this aviation-compliant LMS used for training and certification management.\n\n🛠 Stack: Agile, Scrum, GCAA/EASA/FAA/CAAC regulatory compliance\n\nShe defines the product roadmap, owns the backlog, translates regulatory requirements into user stories, and delivers modules for courses, assessments, certification tracking, and analytics.",
    chips: ["DocuTrack project", "Her projects"],
  },

  // ── Education ───────────────────────────────────────────────────────────────
  {
    keywords: ["education", "university", "degree", "study", "studied", "ruhuna", "graduate", "graduated", "academic", "college", "gpa", "major", "qualification"],
    phrases: ["where did she study", "what degree", "university degree", "educational background"],
    response:
      "Navodya holds a **BSc (Hons) in Computer Science** from the **University of Ruhuna**, Sri Lanka 🎓\n\nHer academic work covered software engineering, algorithms, systems design, AI/ML, and computer vision. Her final year project was a full-stack Medical Center Administrative System.",
    chips: ["Final year project", "Who is Navodya?"],
  },

  // ── Contact ─────────────────────────────────────────────────────────────────
  {
    keywords: ["contact", "reach", "email", "message", "touch", "connect", "dm", "write", "send"],
    phrases: ["how to contact", "get in touch", "send a message", "contact navodya", "reach out"],
    response:
      "You can reach Navodya through several ways:\n\n📧 **Email:** navodyase.official@gmail.com\n📱 **Phone:** +971 52 985 8737\n💼 **LinkedIn:** linkedin.com/in/navodya\n🐙 **GitHub:** github.com/navodya1998\n\nOr just use the **Contact Form** on this page — scroll down to the Contacts section!",
    chips: ["Is she hireable?", "Her LinkedIn"],
  },

  // ── Phone ───────────────────────────────────────────────────────────────────
  {
    keywords: ["phone", "call", "number", "mobile", "whatsapp"],
    phrases: ["phone number", "contact number"],
    response: "Navodya's phone number is **+971 52 985 8737** 📱 (Dubai, UAE)",
    chips: ["Email her", "Contact form"],
  },

  // ── Email ───────────────────────────────────────────────────────────────────
  {
    keywords: ["gmail", "mail"],
    phrases: ["email address", "her email"],
    response: "Navodya's email is **navodyase.official@gmail.com** 📧",
    chips: ["Contact form", "LinkedIn"],
  },

  // ── Social links ────────────────────────────────────────────────────────────
  {
    keywords: ["linkedin", "github", "social", "medium", "hackerrank", "facebook", "instagram", "twitter", "profile", "online"],
    phrases: ["social media", "find her online", "her profiles"],
    response:
      "Find Navodya across the web:\n\n💼 **LinkedIn:** linkedin.com/in/navodya\n🐙 **GitHub:** github.com/navodya1998\n✍️ **Medium:** @navodyaaluthdurage\n🏆 **HackerRank:** /ruhHac4\n📘 **Facebook:** navodya.fernando",
    chips: ["Her blogs", "Contact her"],
  },

  // ── Blog / Writing ──────────────────────────────────────────────────────────
  {
    keywords: ["blog", "article", "write", "medium", "post", "read", "publish", "author", "writing"],
    phrases: ["her blog", "her articles", "medium posts", "does she write"],
    response:
      "Navodya writes technical articles on **Medium** ✍️\n\nPublished articles:\n• **OOP Concepts** — A beginner's guide to OOP (Feb 2024)\n• **SOLID Principles with Real World Examples** (Feb 2024)\n• **Getting Started with MERN Stack** — 4-part series covering full-stack CRUD development (Mar 2024)\n\n📖 Read at: medium.com/@navodyaaluthdurage",
    chips: ["MERN articles", "Her GitHub"],
  },

  // ── Hire / Availability ─────────────────────────────────────────────────────
  {
    keywords: ["hire", "available", "open", "opportunity", "job", "position", "role", "recruit", "freelance", "contract", "candidate", "vacancy"],
    phrases: ["is she available", "is she hireable", "open to work", "looking for job", "can i hire", "hiring navodya"],
    response:
      "Navodya is open to exciting **new opportunities**! 🚀\n\nWhether it's full-time roles, contract work, or interesting projects — feel free to reach out:\n\n📧 navodyase.official@gmail.com\n💼 linkedin.com/in/navodya\n\nOr use the **Contact Form** on this page to send a direct message.",
    chips: ["Contact her", "Her skills", "Her experience"],
  },

  // ── Years of experience ──────────────────────────────────────────────────────
  {
    keywords: ["years", "how long", "experience level", "junior", "senior", "mid-level", "seniority"],
    phrases: ["how many years", "years of experience", "how experienced", "experience level"],
    response:
      "Navodya has **3+ years** of professional software engineering experience (2023–present), with additional academic project experience from university. She's worked across full-time roles and internships, building production-grade enterprise systems throughout.",
    chips: ["Work experience", "Her projects"],
  },

  // ── Languages spoken ─────────────────────────────────────────────────────────
  {
    keywords: ["language", "speak", "english", "sinhala", "tamil", "fluent", "communication"],
    phrases: ["what languages does she speak", "spoken language", "language skills"],
    response:
      "Navodya speaks:\n\n• 🇬🇧 **English** — Professional fluency\n• 🇱🇰 **Sinhala** — Native language",
    chips: ["Who is Navodya?", "Contact her"],
  },

  // ── Strengths / Soft skills ──────────────────────────────────────────────────
  {
    keywords: ["strength", "soft skill", "leadership", "teamwork", "communication", "problem solving", "agile", "scrum", "management"],
    phrases: ["her strengths", "soft skills", "leadership skills", "problem solver"],
    response:
      "Beyond technical skills, Navodya brings:\n\n• **Product Ownership** — led LMS product roadmap and stakeholder alignment\n• **Team Leadership** — led hackathon team to finals\n• **Agile / Scrum** — hands-on in sprint planning, backlog management\n• **System Design** — clean architecture, scalable schema design\n• **Communication** — translates technical concepts to non-technical stakeholders",
    chips: ["Work experience", "Who is Navodya?"],
  },

  // ── MERN Stack ──────────────────────────────────────────────────────────────
  {
    keywords: ["mern", "mongo", "express", "full stack", "fullstack"],
    phrases: ["mern stack", "full stack developer", "fullstack experience"],
    response:
      "Navodya has extensive **MERN stack** experience:\n\n• **Biomedical System** — MERN + OpenCV + DICOM\n• **AI Vision System** — React + Node.js + MongoDB + AWS\n• **Medical Center System** — MERN + JWT + Redux\n• **Travel & Tour Website** — MERN + Bootstrap\n\nShe also writes MERN tutorials on Medium — a 4-part series on building CRUD apps.",
    chips: ["Her blogs", "MERN projects"],
  },

  // ── Resume / CV ─────────────────────────────────────────────────────────────
  {
    keywords: ["resume", "cv", "download", "pdf"],
    phrases: ["her resume", "her cv", "download resume", "get cv"],
    response:
      "You can download Navodya's **CV/Resume** directly from this portfolio! Look for the **Download CV** button in the hero section at the top of the page. 📄",
    chips: ["Contact her", "Her experience"],
  },

  // ── Thanks ───────────────────────────────────────────────────────────────────
  {
    keywords: ["thank", "thanks", "appreciate", "helpful", "great", "awesome", "cool", "nice", "perfect", "excellent", "good"],
    phrases: ["thank you", "many thanks", "that's helpful", "that helps"],
    response:
      "You're welcome! 😊 Happy to help. Feel free to ask anything else or use the **Contact Form** to reach Navodya directly.",
    chips: ["More questions", "Contact her"],
  },

  // ── Goodbye ──────────────────────────────────────────────────────────────────
  {
    keywords: ["bye", "goodbye", "ciao", "later", "see you", "ttyl", "farewell"],
    phrases: ["good bye", "see you later", "talk later"],
    response:
      "Goodbye! 👋 It was great chatting with you. Don't hesitate to come back anytime. Have a wonderful day!",
    chips: [],
  },
];

// ── Score-based matching engine ──────────────────────────────────────────────
export function getResponse(input) {
  const lower = input.toLowerCase().trim();

  let bestScore = 0;
  let bestEntry = null;

  for (const entry of responses) {
    let score = 0;

    // Phrase matches score higher (weight 3)
    for (const phrase of entry.phrases || []) {
      if (lower.includes(phrase)) score += 3;
    }

    // Keyword matches (weight 1)
    for (const kw of entry.keywords || []) {
      if (lower.includes(kw)) score += 1;
    }

    if (score > bestScore) {
      bestScore = score;
      bestEntry = entry;
    }
  }

  // Only return a match if score is meaningful
  if (bestScore >= 1 && bestEntry) {
    return { text: bestEntry.response, chips: bestEntry.chips || [] };
  }

  return { text: FALLBACK, chips: QUICK_REPLIES };
}
